import { Controller } from "stimulus"

export default class extends Controller {
  connect() {
    this.openingPanels = new Set()
    this.restoreOpenPanels()
  }

  toggle(event) {
    event.preventDefault()

    const { taskId, taskPosition: position } = event.currentTarget.dataset
    if (!taskId || position === undefined) return

    // 🚫 Prevent double-open while loading
    if (this.openingPanels.has(taskId)) return

    const existingPanel = this.rightColumn.querySelector(
      `li.saved-step[data-task-id="${taskId}"]`
    )

    const { accountId, projectId, taskListId } = this.element.dataset

    if (existingPanel) {
      this.close(taskId)
    } else {
      this.openingPanels.add(taskId)
      this.open(taskId, accountId, projectId, taskListId, position, { restoring: true })
    }
  }

  open(taskId, accountId, projectId, taskListId, position, { restoring = false } = {}) {
    const panelId = this.panelId(taskId)

    const url = this.panelUrl(taskId, accountId, projectId, taskListId, position)

    fetch(url, {
      headers: { Accept: "text/html" },
      credentials: "same-origin"
    })
      .then(r => r.text())
      .then(html => {
        const temp = document.createElement("div")
        temp.innerHTML = html.trim()
        const newPanel = temp.firstElementChild

        this.insertPanelInOrder(newPanel, position)
        this.markActive(taskId)
        this.scrollPanelIntoView(panelId)

        this.updateRightPanelStepNumbers()
        this.persistOpenPanels()

        // ✅ ONLY apply expand state on first open / restore
        if (restoring) {
          this.applyExpandState(newPanel)
        }
      })
      .catch(console.error)
      .finally(() => {
        this.openingPanels.delete(taskId)
      })
  }

  close(taskId) {
    const panel = this.rightColumn.querySelector(
      `li.saved-step[data-task-id="${taskId}"]`
    )

    panel?.remove()
    this.unmarkActive(taskId)
    this.persistOpenPanels()
  }

  panelId(taskId) {
    return `task_${taskId}`
  }

  panelUrl(taskId, accountId, projectId, taskListId, position) {
    return `/${accountId}/projects/${projectId}/task_lists/${taskListId}/tasks/${taskId}/panel?position=${position}`
  }

  get rightColumn() {
    return this.element.querySelector(
      `.process-right ol.compact-task`
    )
  }

  get rightPanelContainer() {
    const firstPanel = document.querySelector(".compact-task-panel")
    return firstPanel?.parentElement
  }

  getOrderedTaskIds() {
    return Array.from(
      document.querySelectorAll(".saved-step")
    ).map(el => el.dataset.taskId)
  }

  markActive(taskId) {
    this.leftItem(taskId)?.classList.add("is-active")
  }

  unmarkActive(taskId) {
    this.leftItem(taskId)?.classList.remove("is-active")
  }

  leftItem(taskId) {
    return document.querySelector(
      `.compact-task-item[data-task-id="${taskId}"]`
    )
  }

  scrollPanelIntoView(panelId) {
    document.getElementById(panelId)?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    })
  }

  taskPosition(taskId) {
    const items = Array.from(
      this.element.querySelectorAll(".compact-task-item")
    )
    return items.findIndex(item => item.dataset.taskId === taskId)
  }

  insertPanelInOrder(newPanel, position) {
    const list = this.rightColumn
    if (!list) return

    const items = Array.from(
      list.querySelectorAll("li.saved-step")
    )

    const beforeItem = items.find(li => {
      const taskId = li.dataset.taskId
      const panelPosition = this.taskPosition(taskId)
      return panelPosition > position
    })

    if (beforeItem) {
      beforeItem.before(newPanel)
    } else {
      list.appendChild(newPanel)
    }
  }

  newTask(event) {
    event.preventDefault()

    const taskListId = event.currentTarget.dataset.taskListId
    const position   = event.currentTarget.dataset.nextPosition

    const { accountId, projectId } = this.element.dataset

    const url = `/${accountId}/projects/${projectId}/task_lists/${taskListId}/tasks/new?position=${position}&compact=true`

    fetch(url, {
      headers: { Accept: "text/javascript" },
      credentials: "same-origin"
    })
  }

  closeNewTask() {
    document
      .getElementById("compact-new-task-panel")
      ?.remove();
  }

  persistOpenPanels() {
    const items = Array.from(
      this.rightColumn.querySelectorAll("li.saved-step")
    )

    const data = items.map(li => ({
      taskId: li.dataset.taskId,
      position: this.taskPosition(li.dataset.taskId)
    }))

    localStorage.setItem(this.storageKey(), JSON.stringify(data))
  }

  restoreOpenPanels() {
    const raw = localStorage.getItem(this.storageKey())
    if (!raw) return

    let panels
    try {
      panels = JSON.parse(raw)
    } catch {
      return
    }

    const { accountId, projectId, taskListId } = this.element.dataset

    panels
      .sort((a, b) => a.position - b.position)
      .forEach(({ taskId, position }) => {
        this.open(
          taskId,
          accountId,
          projectId,
          taskListId,
          position
        )
      })

    setTimeout(() => {
      this.updateRightPanelStepNumbers()
    }, 0)
  }

  storageKey() {
    const { accountId, projectId, taskListId } = this.element.dataset
    return `compact-task-panels:${accountId}:${projectId}:${taskListId}`
  }

  updateRightPanelStepNumbers() {
    const orderedTaskIds = Array.from(
      this.element.querySelectorAll(".compact-task-item")
    ).map(item => item.dataset.taskId)

    orderedTaskIds.forEach((taskId, index) => {
      const li = this.rightColumn.querySelector(
        `li[data-task-id="${taskId}"]`
      )
      if (!li) return

      li.querySelectorAll(".compact-step-number").forEach(el => {
        el.textContent = index + 1
      })
    })
  }

  reorderPanels() {
    const list = this.rightColumn
    if (!list) return

    // Order from LEFT side
    const leftOrder = Array.from(
      document.querySelectorAll(".compact-task-item")
    ).map(el => el.dataset.taskId)

    // Map existing RIGHT panels
    const panelByTaskId = {}
    list.querySelectorAll("li.saved-step").forEach(li => {
      panelByTaskId[li.dataset.taskId] = li
    })

    // Re-append ONLY existing panels in correct order
    leftOrder.forEach(taskId => {
      const li = panelByTaskId[taskId]
      if (li) {
        list.appendChild(li)
      }
    })

    this.updateRightPanelStepNumbers()
  }

  applyExpandState(li) {
    const step = li.querySelector(".step")
    if (!step) return

    const completed = step.dataset.taskCompleted === "true"
    completed ? this.forceCollapse(step) : this.forceExpand(step)
  }

  forceExpand(step) {
    step.dataset.stepExpanderExpandedValue = "true"
    step.dataset.stepExpanderExpanded = "true"
    step.dataset.stepExpanderStatus = "expanded"

    step.classList.add("expanded")
    step.classList.remove("minimized")
  }

  forceCollapse(step) {
    step.dataset.stepExpanderExpandedValue = "false"
    step.dataset.stepExpanderExpanded = "false"
    step.dataset.stepExpanderStatus = "minimized"

    step.classList.remove("expanded")
    step.classList.add("minimized")
  }

  insertTask(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const previousTaskId = button.dataset.previousTaskId
    const position = button.dataset.insertPosition

    const { accountId, projectId, taskListId } = this.element.dataset

    const url =
      `/${accountId}/projects/${projectId}/task_lists/${taskListId}/tasks/new` +
      `?compact=true&previous_step=${previousTaskId}&position=${position}`

    fetch(url, {
      headers: { Accept: "text/javascript" },
      credentials: "same-origin"
    })
  }
}
